import z from "zod";
import GreetingMembershipService from "../../../../services/GreetingMembershipService";
import { mapErrorsToFields } from "../../../../utils";
import { AxiosError } from "axios";

const api = new GreetingMembershipService();

export type State = {
  message: string | null;
  errors?: {
    name?: string[];
    description?: string[];
    price?: string[];
    duration?: string[];
    eGreets?: string[];
    active?: string[];
    color?: string[];
    general?: string[];
  };
  success: boolean;
  defaultValues: {
    id?: string;
    name?: string;
    description?: string;
    price?: number;
    duration?: number;
    eGreets?: number;
    active?: boolean;
    color?: string;
  };
};

const FormSchema = z.object({
  name: z.string().min(1, { message: "Name is required." }),
  description: z.string().optional(),
  price: z.number().min(1, { message: "Price must be greater than 0." }),
  duration: z.number().min(0, { message: "Duration must be greater than or equal to 0." }).optional(),
  eGreets: z.number().min(0, { message: "EGreets must be greater than or equal to 0." }).optional(),
  active: z.boolean().optional(),
  color: z.string().max(50, { message: "Color cannot exceed 50 characters." }).optional(),
});

function parseFormData(formData: FormData) {
  const priceRaw = formData.get("price")?.toString();
  const durationRaw = formData.get("duration")?.toString();
  const eGreetsRaw = formData.get("eGreets")?.toString();

  return {
    name: formData.get("name")?.toString() || "",
    description: formData.get("description")?.toString() || "",
    price: priceRaw ? parseInt(priceRaw, 10) : undefined,
    duration: durationRaw ? parseInt(durationRaw, 10) : undefined,
    eGreets: eGreetsRaw ? parseInt(eGreetsRaw, 10) : undefined,
    active: formData.get("active") === "on",
    color: formData.get("color")?.toString() || undefined,
  };
}

export async function createGreetingMembership(
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = parseFormData(formData);

  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to create greeting membership.",
        defaultValues,
        success: false,
      };
    }

    const result = await api.createGreetingMembership(validatedFields.data);
    if (result.status === 201) {
      return {
        errors: {},
        message: "Greeting membership created successfully.",
        defaultValues: {
          id: result.data.id,
          ...defaultValues,
        },
        success: true,
      };
    }

    if (result.status === 400 || result.status === 409) {
      return {
        errors: {},
        message: result.data?.message ?? result.data?.description ?? "Failed to create greeting membership.",
        defaultValues,
        success: false,
      };
    }
  } catch (error :any) {
    console.error("Error creating greeting membership:", error.response?.data.errors);
    return {
      errors:  {
        duration: error.response?.data.errors.Duration,
        eGreets: error.response?.data.errors.EGreets,
        price: error.response?.data.errors.Price,
        name: error.response?.data.errors.Name,
        description: error.response?.data.errors.Description,
        active: error.response?.data.errors.Active,
        color: error.response?.data.errors.Color,
      } ,
      message: "Failed to create greeting membership.",
      defaultValues,
      success: false,
    };
  }

  return {
    errors: {},
    message: "Failed to create greeting membership.",
    defaultValues,
    success: false,
  };
}

export async function updateGreetingMembership(
  id: string,
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = parseFormData(formData);

  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update greeting membership.",
        defaultValues,
        success: false,
      };
    }

    const result = await api.updateGreetingMembership({
      id,
      ...validatedFields.data,
    });

    if (result.status === 204) {
      return {
        errors: {},
        message: "Greeting membership updated successfully.",
        defaultValues,
        success: true,
      };
    }

    if (result.status === 400 || result.status === 409) {
      return {
        errors: {},
        message: result.data?.message ?? result.data?.description ?? "Failed to update greeting membership.",
        defaultValues,
        success: false,
      };
    }
  } catch (error) {
    console.error("Error updating greeting membership:", error);
  }

  return {
    errors: {},
    message: "Failed to update greeting membership.",
    defaultValues,
    success: false,
  };
}

export async function deleteGreetingMembership(
  id: string
): Promise<{ success: boolean }> {
  try {
    const result = await api.deleteGreetingMembership(id);
    return { success: result.status === 204 };
  } catch (error) {
    console.error("Database Error:", error);
  }

  return { success: false };
}
